import { useEffect, useState } from "react";

/*
 * My Study: what a reader has chosen to keep -- people, pages and passages,
 * each with an optional note -- and which steps of each study path they have
 * walked.
 *
 * Two places to keep it. Signed out, it is localStorage, the same as
 * recentExplorers and activeStudyPath: it survives closing the tab but lives in
 * one browser on one device. Signed in, it is the reader's account (Firestore,
 * through src/firebase/studySync.js), so the same notes are there on any
 * device. Everything below reads and writes through one switch, so the save
 * buttons, note fields and My Study page do not need to know which it is.
 *
 * Recents are what the reader happened to look at; this is what they decided
 * to keep. The two are stored apart so that browsing never pushes a saved item
 * out, and clearing one never touches the other.
 */

const SAVED_KEY = "myStudySaved";
const PATH_PROGRESS_KEY = "myStudyPathProgress";

/* Fired on every write, so each mounted save button and the My Study page stay
   in step without a context provider wrapped around the whole app. */
const CHANGE_EVENT = "ctb:myStudyChange";

/* A note is a study note, not a document. The cap keeps one runaway paste from
   eating the storage quota the rest of the site shares. */
export const MAX_NOTE_LENGTH = 2000;

function getStorage() {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }

  return { getItem: () => null, setItem() {}, removeItem() {} };
}

const storage = getStorage();

/*
 * Set while a reader is signed in: the account's copy of their study, kept in
 * memory so reads stay synchronous, and the adapter that writes changes back.
 * Writes update the memory copy first, so the page answers at once; the
 * account's own snapshot then replaces it when the write lands.
 */
let cloud = null;

function notifyChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }
}

/* A failed account write should not break the page, but it should not vanish
   silently either -- the next snapshot puts the true state back on screen. */
function sendToCloud(write) {
  try {
    Promise.resolve(write()).catch((error) => {
      console.warn("My Study: saving to your account failed", error);
    });
  } catch (error) {
    console.warn("My Study: saving to your account failed", error);
  }
}

function readJson(key, fallback) {
  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // fail silently -- a full or blocked store should not break the page
  }

  notifyChange();
}

/**
 * One key per thing saved, so saving twice is a no-op and the button can ask
 * "is this saved?" without knowing what kind of thing it is looking at.
 */
export function studyItemKey(item) {
  if (!item?.type) return "";

  if (item.type === "person") return `person:${item.id}`;
  if (item.type === "passage") {
    return `passage:${String(item.reference || "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()}`;
  }

  return `page:${item.path}`;
}

/**
 * A passage record for something known by a name and a Scripture reference:
 * { title: "Water to Blood", reference: "Exodus 7:14-25" }. Several references
 * are joined with "; ", which Bible Gateway reads as one passage list. Null
 * when there is no reference to save.
 */
export function passageItem(title, reference) {
  const refs = (Array.isArray(reference) ? reference : [reference])
    .map((ref) => String(ref || "").trim())
    .filter(Boolean);

  if (!refs.length) return null;

  const joined = refs.join("; ");
  return {
    type: "passage",
    reference: joined,
    title: title && title !== joined ? String(title) : "",
  };
}

/** A person record, from anything with an id and a name. */
export function personItem(person) {
  if (!person?.id || !person?.name) return null;

  return {
    type: "person",
    id: person.id,
    name: person.name,
    title: person.title || "",
    era: person.era || "",
  };
}

function isValidItem(item) {
  if (item?.type === "person") return Boolean(item.id && item.name);
  if (item?.type === "passage") return Boolean(item.reference);
  if (item?.type === "page") return Boolean(item.path && item.title);
  return false;
}

function readLocalItems() {
  const items = readJson(SAVED_KEY, []);
  return Array.isArray(items) ? items.filter(isValidItem) : [];
}

function readLocalPathProgress() {
  const progress = readJson(PATH_PROGRESS_KEY, {});
  return progress && typeof progress === "object" ? progress : {};
}

export function getSavedItems() {
  return cloud ? cloud.items : readLocalItems();
}

export function isSaved(item) {
  const key = studyItemKey(item);
  return Boolean(key) && getSavedItems().some((entry) => entry.key === key);
}

export function saveItem(item) {
  if (!isValidItem(item)) return;

  const key = studyItemKey(item);
  const existing = getSavedItems();
  if (existing.some((entry) => entry.key === key)) return;

  const entry = { ...item, key, note: "", savedAt: Date.now() };

  if (cloud) {
    cloud.items = [entry, ...existing];
    notifyChange();
    sendToCloud(() => cloud.adapter.putItem(entry));
    return;
  }

  writeJson(SAVED_KEY, [entry, ...existing]);
}

export function removeItem(key) {
  const remaining = getSavedItems().filter((entry) => entry.key !== key);

  if (cloud) {
    cloud.items = remaining;
    notifyChange();
    sendToCloud(() => cloud.adapter.deleteItem(key));
    return;
  }

  writeJson(SAVED_KEY, remaining);
}

/** Saves or unsaves, and returns whether the item is now saved. */
export function toggleSaved(item) {
  if (isSaved(item)) {
    removeItem(studyItemKey(item));
    return false;
  }

  saveItem(item);
  return true;
}

export function updateNote(key, note) {
  const text = String(note || "").slice(0, MAX_NOTE_LENGTH);
  let changed = null;

  const items = getSavedItems().map((entry) => {
    if (entry.key !== key) return entry;
    changed = { ...entry, note: text, notedAt: Date.now() };
    return changed;
  });

  if (cloud) {
    if (!changed) return;
    cloud.items = items;
    notifyChange();
    sendToCloud(() => cloud.adapter.putItem(changed));
    return;
  }

  writeJson(SAVED_KEY, items);
}

/*
 * Study path progress is the set of steps visited, not the furthest step.
 * Paths can be entered part way through -- a reader arriving at step 4 from a
 * search result and then walking on has not read steps 1 to 3, and the page
 * should not tell them they have.
 */
export function getPathProgress() {
  return cloud ? cloud.pathProgress : readLocalPathProgress();
}

function writePathProgress(progress) {
  if (cloud) {
    cloud.pathProgress = progress;
    notifyChange();
    sendToCloud(() => cloud.adapter.putPathProgress(progress));
    return;
  }

  writeJson(PATH_PROGRESS_KEY, progress);
}

export function markPathStepVisited(pathId, route) {
  if (!pathId || !route) return;

  const progress = getPathProgress();
  const visited = Array.isArray(progress[pathId]?.visited)
    ? progress[pathId].visited
    : [];

  if (visited.includes(route)) return;

  writePathProgress({
    ...progress,
    [pathId]: { visited: [...visited, route], updatedAt: Date.now() },
  });
}

export function resetPathProgress(pathId) {
  const { [pathId]: _removed, ...rest } = getPathProgress();
  writePathProgress(rest);
}

export function clearMyStudy() {
  if (cloud) {
    cloud.items = [];
    cloud.pathProgress = {};
    notifyChange();
    sendToCloud(() => cloud.adapter.clearAll());
    return;
  }

  clearLocalStudy();
}

/* ---- Accounts -------------------------------------------------------------
 * Called by src/firebase/studySync.js; nothing else needs these.
 */

/**
 * Switches My Study to the signed-in reader's account. The adapter does the
 * writing: { putItem(entry), deleteItem(key), putPathProgress(progress),
 * clearAll() }, each returning a promise. `initial` is what to show until the
 * account's first snapshot arrives.
 */
export function attachCloud(adapter, initial = {}) {
  cloud = {
    adapter,
    items: Array.isArray(initial.items) ? initial.items.filter(isValidItem) : [],
    pathProgress:
      initial.pathProgress && typeof initial.pathProgress === "object"
        ? initial.pathProgress
        : {},
  };
  notifyChange();
}

/** Back to this browser's own copy, on sign-out. */
export function detachCloud() {
  if (!cloud) return;
  cloud = null;
  notifyChange();
}

export function isCloudAttached() {
  return Boolean(cloud);
}

/** The account's current state, from its live snapshot. Ignored once signed out. */
export function applyCloudSnapshot({ items, pathProgress } = {}) {
  if (!cloud) return;

  if (Array.isArray(items)) {
    cloud.items = items
      .filter(isValidItem)
      .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
  }
  if (pathProgress && typeof pathProgress === "object") {
    cloud.pathProgress = pathProgress;
  }

  notifyChange();
}

/** This browser's own copy, whichever is showing -- for the first-sign-in import. */
export function getLocalStudy() {
  return { items: readLocalItems(), pathProgress: readLocalPathProgress() };
}

export function clearLocalStudy() {
  storage.removeItem(SAVED_KEY);
  storage.removeItem(PATH_PROGRESS_KEY);
  notifyChange();
}

/*
 * What a browser's own study adds to an account's, on sign-in, so nobody loses
 * work they did before they had an account -- or on another browser signed
 * out. Nothing in the account is ever dropped:
 * - an item only this browser has is added;
 * - an item both have keeps the account's copy, but a note only this browser
 *   has is carried over, and two different notes are both kept (the account's
 *   first) when they fit together; when they do not, the newer one wins;
 * - path progress is the union of the steps visited in each.
 *
 * Returns only what has to be written: { items, pathProgress } where
 * pathProgress is null when the account's already covers this browser's.
 */
export function mergeStudy(local = {}, account = {}) {
  const accountItems = new Map(
    (account.items || []).filter(isValidItem).map((entry) => [entry.key, entry])
  );
  const items = [];

  (local.items || []).filter(isValidItem).forEach((entry) => {
    const theirs = accountItems.get(entry.key);

    if (!theirs) {
      items.push(entry);
      return;
    }

    const mine = String(entry.note || "").trim();
    const kept = String(theirs.note || "").trim();
    if (!mine || mine === kept) return;

    let note;
    if (!kept) {
      note = mine;
    } else if (kept.length + mine.length + 2 <= MAX_NOTE_LENGTH) {
      note = `${kept}\n\n${mine}`;
    } else if ((entry.notedAt || 0) > (theirs.notedAt || 0)) {
      note = mine;
    } else {
      return;
    }

    items.push({
      ...theirs,
      note,
      notedAt: Math.max(entry.notedAt || 0, theirs.notedAt || 0) || Date.now(),
    });
  });

  const accountProgress = account.pathProgress || {};
  const localProgress = local.pathProgress || {};
  const merged = { ...accountProgress };
  let progressChanged = false;

  Object.entries(localProgress).forEach(([pathId, value]) => {
    const visited = Array.isArray(value?.visited) ? value.visited : [];
    const have = Array.isArray(merged[pathId]?.visited)
      ? merged[pathId].visited
      : [];
    const added = visited.filter((route) => !have.includes(route));
    if (!added.length) return;

    progressChanged = true;
    merged[pathId] = {
      visited: [...have, ...added],
      updatedAt: Math.max(value?.updatedAt || 0, merged[pathId]?.updatedAt || 0),
    };
  });

  return { items, pathProgress: progressChanged ? merged : null };
}

/**
 * The saved items and path progress, re-read whenever either changes -- in this
 * tab through CHANGE_EVENT, in another tab through the browser's storage event.
 */
export function useMyStudy() {
  const [snapshot, setSnapshot] = useState(() => ({
    items: getSavedItems(),
    pathProgress: getPathProgress(),
    inAccount: isCloudAttached(),
  }));

  useEffect(() => {
    function refresh() {
      setSnapshot({
        items: getSavedItems(),
        pathProgress: getPathProgress(),
        inAccount: isCloudAttached(),
      });
    }

    /* Another tab's localStorage write only matters while this one is reading
       localStorage; signed in, the account's snapshot keeps tabs in step. */
    function handleStorage(event) {
      if (cloud) return;
      if (event.key === null || event.key === SAVED_KEY || event.key === PATH_PROGRESS_KEY) {
        refresh();
      }
    }

    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return snapshot;
}
